/**
 * CoachIncomeForm — the zero-tap income entry point on the public /coach screen.
 *
 * The live funnel showed 124/127 visitors bounced without typing, yet everyone
 * who DID engage asked the same thing: "is my covered-call income real, or am I
 * eating my principal?" This form removes the friction: it's visible inline on
 * load (no tap to start), collects the fixed position schema with proper inputs
 * (date picker, numeric fields), then composes a plain-English message and sends
 * it through the normal chat — which calls the compute_income_split tool and
 * answers with the real numbers. Structured input in, conversational answer out.
 *
 * No backend: onSubmit just hands the composed question to the parent's send().
 */
import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** "2026-08-21" → "Aug 21 · 56d" for the expiry dropdown. */
function fmtExpiry(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  const dte = Math.max(0, Math.round((d.getTime() - Date.now()) / 86_400_000));
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · ${dte}d`;
}

export interface IncomeFormFields {
  symbol: string;
  type: 'call' | 'put';
  strike: string;
  expiry: string;
  premium: string;
  costBasis: string;
  contracts: string;
  entryDate: string;
}

interface CoachIncomeFormProps {
  onSubmit: (fields: IncomeFormFields) => void;
  disabled?: boolean;
}

const CoachIncomeForm: React.FC<CoachIncomeFormProps> = ({ onSubmit, disabled }) => {
  const [ticker, setTicker] = useState('');
  const [optType, setOptType] = useState<'call' | 'put'>('call');
  const [strike, setStrike] = useState('');
  const [expiry, setExpiry] = useState('');
  const [premium, setPremium] = useState('');
  const [costBasis, setCostBasis] = useState('');
  const [contracts, setContracts] = useState('1');
  const [entryDate, setEntryDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Real listed expiries for this ticker → the expiry field is a dropdown of dates
  // that actually exist. expiriesChecked tracks whether a fetch has resolved for
  // the current ticker (so we can tell "no ticker yet" from "ticker has no options").
  const [expiries, setExpiries] = useState<string[]>([]);
  const [expiriesChecked, setExpiriesChecked] = useState(false);

  const isCsp = optType === 'put';
  const tickerValid = /^[A-Z]{1,5}$/.test(ticker.trim().toUpperCase());

  // Fetch the ticker's listed expiries (debounced) whenever the ticker or call/put
  // changes.
  useEffect(() => {
    const t = ticker.trim().toUpperCase();
    setExpiriesChecked(false);
    if (!/^[A-Z]{1,5}$/.test(t)) {
      setExpiries([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/public/coach/expiries?symbol=${encodeURIComponent(t)}&type=${optType}`,
        );
        const data = await res.json().catch(() => null);
        if (!cancelled) {
          setExpiries(Array.isArray(data?.expiries) ? data.expiries : []);
          setExpiriesChecked(true);
        }
      } catch {
        if (!cancelled) {
          setExpiries([]);
          setExpiriesChecked(true);
        }
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [ticker, optType]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const t = ticker.trim().toUpperCase();
    if (!t) return setError('Add the ticker (e.g. NVDA).');
    if (!(parseFloat(strike) > 0)) return setError('Strike must be a positive number.');
    if (!expiry) return setError('Pick the expiration date.');
    if (!(parseFloat(premium) > 0) && !entryDate) {
      return setError('Add the premium you collected (per share), or the date you opened it.');
    }
    if (premium.trim() && !(parseFloat(premium) > 0)) return setError('Premium must be a positive number.');
    if (costBasis.trim() && !(parseFloat(costBasis) > 0)) return setError('Cost basis must be a positive number.');
    if (contracts.trim() && !(parseInt(contracts, 10) >= 1)) return setError('Contracts must be at least 1.');
    setError(null);

    onSubmit({
      symbol: t,
      type: optType,
      strike,
      expiry,
      premium,
      costBasis: isCsp ? '' : costBasis.trim(),
      contracts: (contracts || '1').trim() || '1',
      entryDate,
    });
  };

  return (
    <form className="cch-iform" onSubmit={handleSubmit}>
      <p className="cch-iform-title">Is your covered-call income real — or are you eating your principal?</p>
      <p className="cch-iform-sub">
        Fill in your position and I’ll do the actual math — what’s real income versus just your shares moving.
      </p>

      <div className="cch-iform-grid">
        <label className="cch-iform-field cch-iform-ticker">
          <span className="cch-iform-label">Ticker</span>
          <input
            className="cch-iform-input"
            type="text"
            placeholder="NVDA"
            autoCapitalize="characters"
            value={ticker}
            onChange={(e) => {
              setTicker(e.target.value);
              setExpiry(''); // a new ticker has different listed expiries
            }}
          />
        </label>

        <div className="cch-iform-field cch-iform-type">
          <span className="cch-iform-label">Type</span>
          <div className="cch-iform-toggle" role="group" aria-label="Option type">
            <button
              type="button"
              className={`cch-iform-toggle-btn${optType === 'call' ? ' is-on' : ''}`}
              onClick={() => { setOptType('call'); setExpiry(''); }}
            >
              Call
            </button>
            <button
              type="button"
              className={`cch-iform-toggle-btn${optType === 'put' ? ' is-on' : ''}`}
              onClick={() => { setOptType('put'); setExpiry(''); }}
            >
              Put
            </button>
          </div>
        </div>

        <label className="cch-iform-field">
          <span className="cch-iform-label">Strike</span>
          <input
            className="cch-iform-input"
            type="number"
            inputMode="decimal"
            placeholder="230"
            value={strike}
            onChange={(e) => setStrike(e.target.value)}
          />
        </label>

        <label className="cch-iform-field">
          <span className="cch-iform-label">Expires</span>
          {expiries.length > 0 ? (
            // Real listed expiries for this ticker.
            <select
              className="cch-iform-input"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
            >
              <option value="">Select…</option>
              {expiries.map((d) => (
                <option key={d} value={d}>{fmtExpiry(d)}</option>
              ))}
            </select>
          ) : tickerValid && expiriesChecked ? (
            // Ticker entered but no listed options (off-universe) → free date input.
            <input
              className="cch-iform-input"
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
            />
          ) : (
            // No ticker yet / loading → a disabled dropdown so it never reads as a
            // bare calendar.
            <select className="cch-iform-input" disabled value="">
              <option>{tickerValid ? 'Loading expiries…' : 'Enter ticker first…'}</option>
            </select>
          )}
        </label>

        <label className="cch-iform-field">
          <span className="cch-iform-label">Premium collected <span className="cch-iform-hint-inline">/ share — or give the date</span></span>
          <input
            className="cch-iform-input"
            type="number"
            inputMode="decimal"
            placeholder="6.20"
            value={premium}
            onChange={(e) => setPremium(e.target.value)}
          />
        </label>

        <label className={`cch-iform-field${isCsp ? ' is-disabled' : ''}`}>
          <span className="cch-iform-label">
            {isCsp ? 'Cost basis (n/a for puts)' : 'Cost basis'} <span className="cch-iform-hint-inline">/ share</span>
          </span>
          <input
            className="cch-iform-input"
            type="number"
            inputMode="decimal"
            placeholder="182"
            value={costBasis}
            disabled={isCsp}
            onChange={(e) => setCostBasis(e.target.value)}
          />
        </label>

        <label className="cch-iform-field">
          <span className="cch-iform-label">Contracts</span>
          <input
            className="cch-iform-input"
            type="number"
            inputMode="numeric"
            min="1"
            placeholder="1"
            value={contracts}
            onChange={(e) => setContracts(e.target.value)}
          />
        </label>

        <label className="cch-iform-field">
          <span className="cch-iform-label">Opened on <span className="cch-iform-hint-inline">optional</span></span>
          <input
            className="cch-iform-input"
            type="date"
            value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
          />
        </label>
      </div>

      {error && <p className="cch-iform-err">{error}</p>}

      <button className="cch-iform-go" type="submit" disabled={disabled}>
        Show me my real income →
      </button>
      <p className="cch-iform-or">or just type your question below</p>
    </form>
  );
};

export default CoachIncomeForm;
